import { LoadingOutlined } from '@ant-design/icons'
import { useLazyQuery, useMutation } from '@apollo/client'
import { FieldInput } from 'components/ReduxFormComponents'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Field } from 'redux-form'
// hixme-ui
import FormGroup from '@hixme-ui/form-group'
import Select from '@hixme-ui/select'
import { Stack, Typography } from '@sureco/design-system'
import {
  Alert,
  Button,
  Input,
  Spin,
  notification,
} from '@surecompanies/core_components'
import {
  CALCULATE_TERMINATION_DATES,
  CONFIRM_EMP_TERMINATION,
  SEND_HRIS_TERMINATION_EVENT,
} from 'apollo/queries'
import { enqueueTerminationRequest } from 'enrollment-platform/pages/CensusManagement/api/termination-tracking-enrollment-platform-api-client'
import {
  isFutureMonthTermination,
  onHoldTerminationMessage,
  terminationEffectiveDateDisplay,
} from 'enrollment-platform/pages/CensusManagement/utils/future-month-termination'
import { CustomActionNames, datadog } from 'helpers/datadog'
import dayjs, { convertToText } from 'helpers/datetime'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { isEmpty, isNil } from 'lodash'
import { useActivityLogPopup } from 'modules/activity-log-service'
import { ActivityLogAction } from 'modules/activity-log-service/constants'
import { postActivityLog } from 'modules/activity-log-service/helpers'
import modal from 'modules/app-core/modal'
import { browserHistory } from 'react-router'

const EmpTerminationForm = ({ empData, dispatch, isTestUser }) => {
  const { flags } = useFeatureFlags()
  const isNewTerminationFlow = flags['product.termination.new-flow']
  const [isDisableConfirm, setIsDisableConfirm] = useState(true)
  const [employmentStatus, setEmpStatus] = useState('TERMINATED')
  const [lastDayWorked, setLastDayWorked] = useState()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [empStatusConfirm, setempStatusConfirm] = useState()
  const [dateError, setDateError] = useState('')
  const [calcDates, setCalcDates] = useState({
    previousCoverageEndDate: '',
    cobraBenefitsEffectiveDate: '',
    cobraBenefitsEligibleStartDate: '',
    cobraBenefitsEligibleEndDate: '',
  })
  const { showConfirmationPopup } = useActivityLogPopup()

  const coverageSummaryUrl = `/employees/${empData.id}/coverage`
  // Short-term workaround: only 'Terminated' is functionally wired end-to-end
  // in the backend termination flows. The other statuses were removed from
  // this dropdown until the loss-of-eligibility feature supports them.
  const TerminationStatusList = [
    { id: 'TERMINATED', name: 'Terminated' },
    //  { id: 'PART_TIME', name: 'PartTime' },
    //  { id: 'FULL_TIME', name: 'Full Time' },
    //  { id: 'RETIRED', name: 'Retired' },
    //  { id: 'TERMINATED_NO_COBRA', name: 'Terminated No COBRA' },
    //  { id: 'LEAVE', name: 'Leave' },
    // { id: 'NON_PAID_LEAVE_OF_ABSENCE', name: 'Non Paid Leave Of Absence' },
  ]

  const [reqCalculateTerminationDates] = useLazyQuery(
    CALCULATE_TERMINATION_DATES
  )
  const [reqConfirmTermination] = useMutation(CONFIRM_EMP_TERMINATION)
  const [sendHrisTerminationEvent] = useMutation(SEND_HRIS_TERMINATION_EVENT)

  const isLastDayWorkedValid = useMemo(() => {
    if (!dayjs(lastDayWorked).isValid()) {
      setDateError('Date is not valid')
      return false
    }

    const today = dayjs()
    const selectedDate = dayjs(lastDayWorked)
    const sixMonthsFromNow = today.add(6, 'months')
    const twoYearsAgo = today.subtract(2, 'years')

    if (selectedDate.isAfter(sixMonthsFromNow)) {
      setDateError('Date cannot be more than 6 months in the future')
      return false
    }

    if (selectedDate.isBefore(twoYearsAgo)) {
      setDateError('Date cannot be more than 2 years in the past')
      return false
    }

    setDateError('')
    return true
  }, [lastDayWorked])

  const isValidated = useMemo(() => {
    return (
      isLastDayWorkedValid &&
      !isNil(empStatusConfirm) &&
      !isEmpty(empStatusConfirm) &&
      empStatusConfirm === employmentStatus
    )
  }, [employmentStatus, isLastDayWorkedValid, empStatusConfirm])

  const getCalculatedDates = useCallback(async () => {
    setError(false)
    if (
      isLastDayWorkedValid &&
      employmentStatus &&
      lastDayWorked &&
      employmentStatus !== '' &&
      lastDayWorked !== ''
    ) {
      setLoading(true)
      try {
        await reqCalculateTerminationDates({
          variables: {
            terminationDate: dayjs(lastDayWorked).toISOString(),
          },
        }).then((result) => {
          if (result.data && !result.data.computeCobraDates) {
            setError('Error While compute COBRA dates')
          } else {
            setCalcDates({ ...result.data.computeCobraDates })
            setIsDisableConfirm(false)
          }
        })
      } catch (e) {
        setError('Failed to fetch COBRA dates')
      } finally {
        setLoading(false)
      }
    }
  }, [lastDayWorked])

  useEffect(() => {
    getCalculatedDates()
  }, [employmentStatus, lastDayWorked])

  const onConfirmEmpTermination = useCallback(async () => {
    const callback = async (formData) => {
      const { reason, reasonCategory, zendeskTicket } = formData

      setError(false)
      if (empStatusConfirm === employmentStatus) {
        setLoading(true)
        try {
          const input = {
            terminationDate: dayjs(lastDayWorked).toISOString(),
            id: empData.id,
            employmentStatus,
            cobraBenefitsEffectiveDate: calcDates.cobraBenefitsEffectiveDate,
            cobraBenefitsEligibleStartDate:
              calcDates.cobraBenefitsEligibleStartDate,
            cobraBenefitsEligibleEndDate:
              calcDates.cobraBenefitsEligibleEndDate,
          }
          // The new flow queues the request in Enrollment Platform and progresses on
          // the termination tracking dashboard, so the click is no longer synchronous
          // with the termination itself. The popup Zendesk number is an activity-log
          // reference only — never a ticket for pdf-graph to reuse.
          const terminationHandler = isNewTerminationFlow
            ? () =>
                enqueueTerminationRequest({
                  employeePublicKey: empData.id,
                  clientPublicKey: empData.clientPublicKey,
                  terminationDate: dayjs(lastDayWorked).format('YYYY-MM-DD'),
                  employeeName: empData.fullName,
                  companyEmployeeId:
                    empData.companyEmployeeId ?? empData.CompanyEmployeeId,
                })
            : () => reqConfirmTermination({ variables: { input } })
          const defaultNewFlowNotification =
            'Request received. Any changes to Employment Status or Benefit End Dates will appear after a SureCo Administrator reviews and completes this update, usually within 1 business day.'

          await Promise.resolve(terminationHandler())
            .then(async (created) => {
              const isOnHold =
                Boolean(
                  created &&
                    (created.state === 'ON_HOLD' ||
                      created.status === 'HELD_FUTURE_DATED')
                ) ||
                (isNewTerminationFlow &&
                  isFutureMonthTermination(lastDayWorked))
              const effectiveDate = terminationEffectiveDateDisplay(
                (created && created.terminationDate) || lastDayWorked
              )
              const successNotification =
                isOnHold && effectiveDate
                  ? onHoldTerminationMessage(effectiveDate)
                  : isNewTerminationFlow
                    ? defaultNewFlowNotification
                    : `Employee ${empData.fullName} has been terminated.`

              return { created, successNotification }
            })
            .then(async ({ successNotification }) => {
              await sendHrisTerminationEvent({
                variables: {
                  enrollMeTerminationEventHandlerInput: {
                    employeePublicKey: empData.id,
                    eventDate: new Date(),
                  },
                },
              })
              return successNotification
            })
            .then((successNotification) => {
              dispatch(modal.actions.hideModal())
              notification.success({
                key: 'term-notify',
                message: successNotification,
                duration: 8,
              })
              postActivityLog({
                action: ActivityLogAction.STOP_COVERAGE,
                description: `stop coverage for ${empData.fullName}`,
                additionalData: {
                  empData,
                  reason,
                  reasonCategory,
                  zendeskTicket,
                },
                targetUserId: empData?.id,
                targetName: empData?.fullName,
              })
              datadog.customAction(CustomActionNames.StopCoverage, {
                isNewTerminationFlow,
                target: datadog.extraPersonInfo(empData),
                input,
              })
              browserHistory.push(coverageSummaryUrl)
            })
        } catch (e) {
          notification.error({
            key: 'term-notify',
            message:
              e.message ?? 'Failed to confirm termination, please try again',
            duration: 10,
          })
          dispatch(modal.actions.hideModal())
        } finally {
          setLoading(false)
        }
      } else {
        setError('Please enter valid employment status to confirm')
      }
    }

    showConfirmationPopup({
      contentText: 'Are you sure you want to stop coverage for this person?',
      onSubmit: callback,
      required: true,
      isShowActivityLogPopup: !isTestUser,
    })
  }, [lastDayWorked, employmentStatus, empStatusConfirm])

  const onEmpStatusChange = (e) => {
    setEmpStatus(e.currentTarget.value)
  }
  const onEmpStatusConfirm = (e) => {
    setempStatusConfirm(e.currentTarget.value)
  }
  const onLastDayWrkChange = (date) => {
    if (date) {
      setLastDayWorked(date.target.value)
    }
  }

  const onCancel = (e) => {
    setError(false)
    e.preventDefault()
    dispatch(modal.actions.hideModal())
  }

  return (
    <Stack space={20}>
      <Stack style={{ margin: 50, textAlign: 'center' }}>
        <Typography size={30} bold>
          Stop Coverage
        </Typography>
      </Stack>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: '20px' }}
        />
      )}
      <Stack space={10}>
        <Stack direction="row" space={10} style={{ alignItems: 'center' }}>
          <Typography size={14}>Employee:</Typography>
          <Typography size={16} bold>
            {empData.fullName}
          </Typography>
        </Stack>
        <Stack direction="row" style={{ justifyContent: 'space-between' }}>
          <Stack style={{ width: '100%' }}>
            <FormGroup>
              <Typography size={14}>Employment Status</Typography>
              <Select
                value={employmentStatus}
                list={TerminationStatusList}
                style={{
                  width: '90%',
                }}
                onChange={onEmpStatusChange}
              />
            </FormGroup>
          </Stack>
          <Stack style={{ width: '100%' }}>
            <FormGroup>
              <Typography size={14}>Last Day Worked</Typography>
              <Field
                name="LastDayWorked"
                type="date"
                value={lastDayWorked}
                component={FieldInput}
                onChange={onLastDayWrkChange}
              />
              {dateError && (
                <Typography
                  size={12}
                  color="#f5222d"
                  style={{ marginTop: '4px' }}
                >
                  {dateError}
                </Typography>
              )}
            </FormGroup>
          </Stack>
        </Stack>
      </Stack>
      {isNewTerminationFlow &&
        isLastDayWorkedValid &&
        isFutureMonthTermination(lastDayWorked) && (
          <Alert
            type="info"
            showIcon
            message={`Termination effective date of ${terminationEffectiveDateDisplay(
              lastDayWorked
            )}. Process pending.`}
          />
        )}

      {loading && (
        <Stack style={{ margin: '10px 0' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </Stack>
      )}
      {isLastDayWorkedValid &&
        calcDates &&
        calcDates.cobraBenefitsEligibleEndDate && (
          <Stack space={20}>
            <Stack
              direction="row"
              style={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography size={16}>Contribution Ends</Typography>
              <Typography bold size={16}>
                {calcDates.previousCoverageEndDate}
                <Typography color="#747474" style={{ marginLeft: '5px' }}>
                  {convertToText(calcDates.previousCoverageEndDate)}
                </Typography>
              </Typography>
            </Stack>
            <Stack
              direction="row"
              style={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography size={16}>COBRA Election Starts</Typography>
              <Typography bold size={16}>
                {calcDates.cobraBenefitsEligibleStartDate}
                <Typography color="#747474" style={{ marginLeft: '5px' }}>
                  {convertToText(calcDates.cobraBenefitsEligibleStartDate)}
                </Typography>
              </Typography>
            </Stack>
            <Stack
              direction="row"
              style={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography size={16}>COBRA Election Ends</Typography>
              <Typography bold size={16}>
                {calcDates.cobraBenefitsEligibleEndDate}
                <Typography color="#747474" style={{ marginLeft: '5px' }}>
                  {convertToText(calcDates.cobraBenefitsEligibleEndDate)}
                </Typography>
              </Typography>
            </Stack>
            <Stack>
              <Typography size={14}>
                Please type{' '}
                <Typography
                  bold
                  size={14}
                >{`"${employmentStatus}"`}</Typography>{' '}
                to confirm
              </Typography>
              <Input
                name="EmpStatusConfirm"
                value={empStatusConfirm}
                onChange={onEmpStatusConfirm}
              />
            </Stack>
          </Stack>
        )}
      <Stack
        direction="row"
        space={10}
        style={{ justifyContent: 'center', marginTop: 20 }}
      >
        <Button onClick={onCancel} secondary>
          Cancel
        </Button>
        <Button
          type="primary"
          disabled={isDisableConfirm || !isValidated}
          onClick={onConfirmEmpTermination}
        >
          Save and close
        </Button>
      </Stack>
    </Stack>
  )
}

export default EmpTerminationForm
